// src/Dashboard/pages/teacher/ListingAdherents/components/AdherentTeacherCard.jsx

import React from "react";
import { FaBirthdayCake, FaCamera, FaLayerGroup, FaCreditCard  } from "react-icons/fa";
import { MdMedicalServices } from "react-icons/md";

import {
  calculateAge,
  getAdherentCategories,
  getAdherentPhoto,
  getStatusMeta,
  getReglementStatusMeta,
} from "../utils/listingAdherentsUtils";

function getGroupPillClasses(group) {
  const color = String(group?.color_groupe || "").trim();

  if (color.startsWith("bg-")) {
    return `${color} text-slate-900 ring-1 ring-black/5 dark:ring-white/10`;
  }

  return "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";
}

function stop(event) {
  event.preventDefault();
  event.stopPropagation();
}

export default function AdherentTeacherCard({
  adherent,
  isBirthday,
  canManage,
  onOpenDetails,
  onManageGroups,
}) {
  const status = getStatusMeta(adherent?.statut_inscription);
  const photo = getAdherentPhoto(adherent);
  const age = calculateAge(adherent?.date_naissance);
  const groups = Array.isArray(adherent?.Groups) ? adherent.Groups : [];
  const { ageCategory, weightCategory } = getAdherentCategories(adherent);

  const reglement = getReglementStatusMeta(
  adherent?.statut_reglement
);

  const openGroups = (event) => {
    stop(event);
    if (canManage) onManageGroups?.(adherent);
  };

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => onOpenDetails?.(adherent)}
      onKeyDown={(event) =>
        event.key === "Enter" && onOpenDetails?.(adherent)
      }
      className="group relative overflow-hidden rounded-2xl bg-white shadow ring-1 ring-slate-200/70 transition hover:-translate-y-0.5 hover:shadow-lg dark:bg-slate-900/70 dark:ring-slate-700/70"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-slate-100 dark:bg-slate-800">
        <img
          src={photo}
          alt={`${adherent?.nom || ""} ${adherent?.prenom || ""}`}
          className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
          loading="lazy"
          onError={(event) => {
            event.currentTarget.src = "/img/user_avatar.jpg";
          }}
        />

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/95 via-slate-950/45 to-transparent p-3 pt-12">
          <p className="line-clamp-1 text-sm font-bold text-white">
            {(adherent?.nom || "").toUpperCase()}
          </p>

          <p className="line-clamp-1 text-sm font-medium text-white/90">
            {adherent?.prenom || "—"}
          </p>
        </div>

        <div className="absolute left-2 top-2 flex flex-col gap-1.5">
          {isBirthday && (
            <span
              title="Anniversaire"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-pink-500 text-white shadow-lg ring-2 ring-white/70"
            >
              <FaBirthdayCake />
            </span>
          )}

          {adherent?.is_blessed && (
            <span
              title="Blessure / absence"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-600 text-white shadow-lg ring-2 ring-white/70"
            >
              <MdMedicalServices />
            </span>
          )}

          {adherent?.autorisation_photo === false && (
            <span
              title="Photo non autorisée"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg ring-2 ring-white/70"
            >
              <FaCamera />
            </span>
          )}
        </div>
      </div>

      
<div className="px-3 pt-3">
  <div
    className={`flex items-center justify-center gap-2 rounded-xl px-2 py-2 text-center text-[11px] font-black ${reglement.className}`}
    title={`Règlement inscription : ${reglement.label}`}
  >
    <FaCreditCard className="shrink-0" />
    <span>{reglement.label}</span>
  </div>
</div>


      <div className="space-y-3 p-3">
        <div className="flex items-center justify-between gap-2">
          <span
            className={`inline-flex max-w-full truncate rounded-full px-2 py-1 text-[10px] font-bold ${status.className}`}
            title={adherent?.statut_inscription || ""}
          >
            {adherent?.statut_inscription || "Sans statut"}
          </span>

          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {Number.isFinite(age) ? `${age} ans` : "—"}
          </span>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <span className="rounded-full bg-blue-50 px-2 py-1 text-[10px] font-bold text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-200 dark:ring-blue-800/50">
            {ageCategory || "Catégorie inconnue"}
          </span>

          <span className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/50">
            {weightCategory || "Poids inconnu"}
          </span>
        </div>

        <div className="flex min-h-[34px] flex-wrap gap-1.5">
          {groups.length === 0 ? (
            <button
              type="button"
              onClick={openGroups}
              disabled={!canManage}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-500 ring-1 ring-slate-200 transition hover:bg-slate-200 disabled:cursor-default dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700"
            >
              <FaLayerGroup />
              Sans groupe
            </button>
          ) : (
            groups.slice(0, 3).map((group) => (
              <button
                key={group.id || group.nom}
                type="button"
                onClick={openGroups}
                disabled={!canManage}
                className={`rounded-full px-2 py-1 text-[10px] font-bold transition hover:scale-[1.03] disabled:cursor-default ${getGroupPillClasses(
                  group
                )}`}
                title={canManage ? "Gérer les groupes" : group.nom}
              >
                {group.nom}
              </button>
            ))
          )}

          {groups.length > 3 && (
            <button
              type="button"
              onClick={openGroups}
              disabled={!canManage}
              className="rounded-full bg-slate-900 px-2 py-1 text-[10px] font-bold text-white disabled:cursor-default dark:bg-slate-200 dark:text-slate-900"
            >
              +{groups.length - 3}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}